import React, { FC } from 'react';
import {
  type TypeBasicValuePropContainerFields,
  TypeImage,
  TypeMediaTextTitle,
  type TypeMediaTextTitleFields,
  TypeProduct,
  TypeVideo,
  TypeTabNavigation,
} from '@/core/generated-types';
import { HeaderText, Button } from '@/components';
import { HeaderSize } from '@/components/ui/HeaderText';
import { usePathname } from '@/navigation';
import { shouldUseYotpoReviews } from '@/utils/helpers';
import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';
import { CarouselProducts } from '@/components/common/Carousel';
import scrollTo from '@/utils/scrollTo';
import { useSubFlowV3Data } from '@/components/subflow-v3/context/SubFlowV3Context';
import type { Entry, EntryFields } from 'contentful';
import ProductValueProps from '@/components/common/ProductAddToCart/ProductValueProps';
import { PlayKitDetailsHeaderTreatment } from './PlayKitDetailsHeaderTreatment';
import { TariffPDPMessage } from '@/components/common/Tariff';
import { TypeTariffSettings } from '@/components/common/types';
import { ProductVariantCopy } from '@/lib/contentful';

interface PlayKitInterface {
  // TODO: Remove along with money_increase-perceived-pk-sub-value feature flag
  variant?: string;
  productDescription?: EntryFields.RichText;
  product: TypeProduct;
  carousel: TypeMediaTextTitle[] | TypeVideo[] | TypeImage[];
  onCTAClick: (productSelected?: TypeProduct) => void;
  aboutHeading: string;
  noCommitmentMessage: string;
  featuresHeading: string;
  enableIndividualPlayKitParts?: boolean;
  tabsComponent?: TypeTabNavigation;
  tariffSettings?: TypeTariffSettings;
  bottomMarkerRef: React.RefObject<HTMLDivElement>;
  subscribeCtaMessage?: EntryFields.RichText;
  productVariantsCopy: ProductVariantCopy | null;
}

interface PlayKitBodyInterface {
  longDescription?: string;
  productValueProps?: Entry<
    TypeBasicValuePropContainerFields | TypeMediaTextTitleFields
  >[];
  featuresHeading: string;
  aboutHeading?: string;
}

export const PlayKitDetailsBody: FC<PlayKitBodyInterface> = ({
  longDescription,
  productValueProps,
  aboutHeading,
  featuresHeading,
}) => {
  return (
    <>
      <HeaderText
        classes="mb-2 md:text-3xl lg:text-3xl"
        size={HeaderSize.H3}
        text={aboutHeading}
      />
      {longDescription && (
        <div
          className="mb-4 leading-6"
          dangerouslySetInnerHTML={{ __html: longDescription }}
          onClick={(e) => {
            const anchor = e.target as HTMLAnchorElement;
            if (anchor.tagName === 'A' && anchor.hash) {
              e.preventDefault(); // Prevent default anchor behavior
              scrollTo({
                id: anchor.hash,
                offSet: -80,
              });
            }
          }}
        />
      )}

      <HeaderText
        classes="mb-2 lg:text-2xl"
        size={HeaderSize.H3}
        text={featuresHeading}
      />
      <ProductValueProps
        productValueProps={productValueProps}
        shouldUsePlayKitStyles
      />
    </>
  );
};

export const PlayKitDetailsHeader: React.FC<PlayKitInterface> = ({
  variant,
  productDescription,
  product,
  carousel,
  onCTAClick,
  aboutHeading,
  noCommitmentMessage,
  featuresHeading,
  tariffSettings,
  enableIndividualPlayKitParts,
  tabsComponent,
  bottomMarkerRef,
  subscribeCtaMessage,
  productVariantsCopy,
}) => {
  const {
    sys: { id },
  } = product;
  const {
    title,
    featuredImage,
    shipMessage,
    cta,
    ageDescription,
    pdpCarousel,
  } = product.fields;
  const pathname = usePathname();
  const {
    api: { updateInitialProduct },
  } = useSubFlowV3Data();

  const handleClick = (productSelected?: TypeProduct) => {
    updateInitialProduct(product);
    onCTAClick(productSelected);
  };

  const carouselProductsComponents = pdpCarousel
    ? pdpCarousel.fields.media
    : [featuredImage, ...carousel];

  // Avoid flicker the variants while flag is loading
  if (typeof variant === 'undefined') {
    return null;
  }

  // This is an A/B test variant running on US only
  // All changes to this component should be made in the treatment variant.
  // See MONEY-1511 for details
  if (String(variant).toLowerCase() === 'treatment') {
    return (
      <PlayKitDetailsHeaderTreatment
        key={product.sys.id}
        product={product}
        productDescription={productDescription}
        carouselProductsComponents={carouselProductsComponents}
        pathname={pathname}
        handleClick={handleClick}
        tariffSettings={tariffSettings}
        enableIndividualPlayKitParts={enableIndividualPlayKitParts}
        tabsComponent={tabsComponent}
        bottomMarkerRef={bottomMarkerRef}
        subscribeCtaMessage={subscribeCtaMessage}
        productVariantsCopy={productVariantsCopy}
      />
    );
  }

  return (
    <div className="lg:flex lg:gap-6">
      <div className="pb-3 -m-6 md:mx-0 lg:p-0 lg:m-0 lg:basis-7/12">
        <CarouselProducts
          name="pdp hero carousel"
          id={id}
          components={carouselProductsComponents}
          key={pathname}
        />
      </div>
      <div className="mt-10 lg:mt-0 lg:basis-5/12">
        <HeaderText text={title} size={HeaderSize.H1} />
        <p className="text-base font-bold md:text-lg">{ageDescription}</p>
        {shouldUseYotpoReviews() && (
          <div className="my-3 md:mb-3">
            <YotpoReviewStars product={product} />
          </div>
        )}
        {shipMessage && (
          <h3
            className="font-bold w-full text-charcoal text-h3 md:text-d-h3"
            role="heading"
            dangerouslySetInnerHTML={{ __html: shipMessage }}
          />
        )}
        <div className="col-span-12">
          {cta && (
            <Button
              type="submit"
              ctaText={cta.fields?.linkText}
              classes="mt-5 h-14 w-full lg:h-16 lg:max-w-[360px] md:mt-8"
              onClick={handleClick}
              fullWidth
            />
          )}

          <TariffPDPMessage
            product={product}
            tariffSettings={tariffSettings}
            alignTextLeft
          />
          <p
            className="pb-2 mt-3 text-sm md:text-base"
            dangerouslySetInnerHTML={{ __html: noCommitmentMessage }}
          />

          <div className="border-b-[1px] border-charcoal-opacity-20 mt-1 mb-4 lg:mt-7 lg:mb-6" />
          <PlayKitDetailsBody
            longDescription={product.fields.longDescription}
            productValueProps={product.fields.productValueProps}
            aboutHeading={aboutHeading}
            featuresHeading={featuresHeading}
          />
        </div>
      </div>
    </div>
  );
};
